import { useEffect, useMemo } from 'react'
import { Alert, DatePicker, Form, Input, Modal, Rate, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  COMMENT_MAX,
  SCORE_MAX,
  SCORE_MIN,
  quarterFirstDay,
  ratePerson,
  rateableQuarters,
  type RatingCriterion,
} from './ratingApi'
import { useQuarterLabel } from './useQuarterLabel'

const DATE_FORMAT = 'YYYY-MM-DD'

interface Props {
  open: boolean
  personId: string
  hiredAt: string
  terminatedAt: string | null
  criteria: readonly RatingCriterion[]
  onClose: () => void
}

interface FormValues {
  quarter: string
  ratedOn: Dayjs
  comment?: string
  scores: Record<string, number | undefined>
}

function optional(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? ''
  return trimmed === '' ? null : trimmed
}

export function RatingModal({ open, personId, hiredAt, terminatedAt, criteria, onClose }: Props) {
  const { t } = useTranslation()
  const quarterLabel = useQuarterLabel()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const selectedQuarter = Form.useWatch('quarter', form)

  const quarters = useMemo(
    () => rateableQuarters(hiredAt, terminatedAt, tenantToday().format(DATE_FORMAT)),
    [hiredAt, terminatedAt],
  )
  const earliestDay = (quarter: string | undefined): string => {
    const start = quarter === undefined ? hiredAt : quarterFirstDay(quarter)
    return start > hiredAt ? start : hiredAt
  }

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({ ratedOn: tenantToday(), comment: '', scores: {} })
  }, [open, form])

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      ratePerson(personId, {
        quarter: values.quarter,
        ratedOn: values.ratedOn.format(DATE_FORMAT),
        comment: optional(values.comment),
        scores: criteria.map((criterion) => ({
          criterionId: criterion.id,
          score: values.scores[criterion.id] ?? 0,
        })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('hr.rating.rateTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      okButtonProps={{ disabled: criteria.length === 0 }}
      confirmLoading={save.isPending}
      okText={t('hr.rating.rateSubmit')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={600}
    >
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      {criteria.length === 0 ? (
        <Alert type="warning" showIcon message={t('hr.rating.noCriteria')} />
      ) : (
        <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
          <Form.Item
            name="quarter"
            label={t('hr.rating.quarter')}
            extra={t('hr.rating.quarterHint')}
            rules={[{ required: true, message: t('hr.rating.validation.quarterRequired') }]}
          >
            <Select<string>
              placeholder={t('hr.rating.quarterPlaceholder')}
              style={{ width: 240 }}
              options={quarters.map((quarter) => ({ value: quarter, label: quarterLabel(quarter) }))}
            />
          </Form.Item>

          <Form.Item
            name="ratedOn"
            label={t('hr.rating.ratedOn')}
            dependencies={['quarter']}
            rules={[
              { required: true, message: t('hr.rating.validation.dateRequired') },
              ({ getFieldValue }) => ({
                validator: (_rule, value?: Dayjs) =>
                  value === undefined ||
                  value.format(DATE_FORMAT) >= earliestDay(getFieldValue('quarter') as string | undefined)
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('hr.rating.validation.dateBeforeQuarter'))),
              }),
            ]}
          >
            <DatePicker
              format={DATE_FORMAT}
              style={{ width: 200 }}
              disabledDate={(day) =>
                day.isAfter(tenantToday(), 'day') || day.format(DATE_FORMAT) < earliestDay(selectedQuarter)
              }
            />
          </Form.Item>

          {criteria.map((criterion) => (
            <Form.Item
              key={criterion.id}
              name={['scores', criterion.id]}
              label={criterion.name}
              extra={criterion.description ?? undefined}
              rules={[
                {
                  validator: (_rule, value?: number) =>
                    value !== undefined && value >= SCORE_MIN && value <= SCORE_MAX
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('hr.rating.validation.scoreRequired'))),
                },
              ]}
            >
              <Rate count={SCORE_MAX} />
            </Form.Item>
          ))}

          <Form.Item
            name="comment"
            label={t('hr.rating.comment')}
            rules={[{ max: COMMENT_MAX, message: t('hr.rating.validation.commentLength') }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
